import React, { FunctionComponent, useEffect, useState } from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { PopupDismissalKeys } from "../../../../../store/onboarding/types";
import { useDispatch } from "react-redux";
import { showConfirmDialog } from "../../../../../store/confirm-dialog/actions";
import { toastr } from "react-redux-toastr";
import { resetDismissedModal, updateGlobalSetting, getAllGlobalSettings } from "../../../../../store/superuser/actions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faSpinner } from "@fortawesome/free-solid-svg-icons";

type OwnProps = {};

type Props = OwnProps;

// Enum for setting types
enum SettingType {
  TOGGLE = "toggle",
  BUTTON = "button",
  SELECT = "select",
}

// Interface for a setting configuration
interface SettingConfig {
  id: string;
  label: string;
  description?: string;
  type: SettingType;
  defaultValue?: any;
  options?: Array<{ value: any; label: string }>; // For select type
  action: (value: any) => any; // Action to dispatch (can return Promise or thunk)
  confirmMessage?: string; // Optional confirmation dialog
  successMessage: (value: any) => string;
  errorMessage?: (error: string) => string;
  disabled?: boolean;
  icon?: any;
}

const GlobalSettings: FunctionComponent<Props> = () => {
  const dispatch = useDispatch();
  
  // State management for all settings
  const [settingsState, setSettingsState] = useState<Record<string, any>>({});
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [isFetchingSettings, setIsFetchingSettings] = useState(true);

  // Settings configuration - easily extendable for future settings
  const settingsConfig: SettingConfig[] = [
    {
      id: "mfaEnable",
      label: "Multi-Factor Authentication",
      description: "Enable or disable two-factor authentication for all users",
      type: SettingType.TOGGLE,
      defaultValue: false,
      action: (enabled: boolean) => dispatch(updateGlobalSetting("mfaEnable", ""+enabled)),
      successMessage: (enabled: boolean) =>
        `Multi-Factor Authentication has been ${enabled ? "enabled" : "disabled"}.`,
      errorMessage: (error: string) =>
        `Failed to update MFA setting: ${error}`,
    },
    // Example: Add more settings here
    // {
    //   id: "sessionTimeout",
    //   label: "Session Timeout",
    //   description: "Set the idle session timeout duration",
    //   type: SettingType.SELECT,
    //   defaultValue: 30,
    //   options: [
    //     { value: 15, label: "15 minutes" },
    //     { value: 30, label: "30 minutes" },
    //     { value: 60, label: "1 hour" },
    //   ],
    //   action: (timeout: number) => dispatch(updateSessionTimeout(timeout)),
    //   successMessage: (timeout: number) => `Session timeout updated to ${timeout} minutes.`,
    // },
  ];

  // Initialize settings state
  useEffect(() => {
    const initializeSettings = async () => {
      const initialState: Record<string, any> = {};
      const initialLoading: Record<string, boolean> = {};
      
      // Set default values first
      settingsConfig.forEach((setting) => {
        initialState[setting.id] = setting.defaultValue;
        initialLoading[setting.id] = false;
      });
      
      setSettingsState(initialState);
      setLoadingStates(initialLoading);
      
      // Fetch current values from API (if endpoint exists)
      try {
        setIsFetchingSettings(true);
        const fetchAction: any = dispatch(getAllGlobalSettings());
        const response = await fetchAction;
        
        // Update state with API values if response is valid
        if (response && typeof response === 'object') {
          const apiValues: Record<string, any> = {};
          
          // Map API response to setting IDs
          settingsConfig.forEach((setting) => {
            // Check if response has the setting directly
            if (response[setting.id] !== undefined) {
              // Handle string boolean values
              if (response[setting.id] === "true" || response[setting.id] === "false") {
                apiValues[setting.id] = response[setting.id] === "true";
              } else {
                apiValues[setting.id] = response[setting.id];
              }
            } 
            // Check if response has a settings array
            else if (response.settings && Array.isArray(response.settings)) {
              const apiSetting = response.settings.find(
                (s: any) => s.key === setting.id || s.name === setting.id
              );
              if (apiSetting) {
                // Parse boolean values if they come as strings
                if (apiSetting.value === "true" || apiSetting.value === "false") {
                  apiValues[setting.id] = apiSetting.value === "true";
                } else {
                  apiValues[setting.id] = apiSetting.value;
                }
              }
            }
          });
          
          // Only merge if we got valid API values
          if (Object.keys(apiValues).length > 0) {
            setSettingsState((prev) => ({ ...prev, ...apiValues }));
            console.log("Global settings loaded from API:", apiValues);
          }
        }
      } catch (error: any) {
        // Handle 404 gracefully - endpoint might not exist yet
        const status = error?.status || error?.response?.status;
        
        if (status === 404) {
          console.warn("Global settings API endpoint not found (404). Using default values.");
          console.warn("If you need to load existing settings, please create the backend endpoint: GET /api/global_settings/");
          // Don't show toast for 404 - this is expected if endpoint doesn't exist yet
        } else {
          console.error("Failed to fetch global settings:", error);
          toastr.warning(
            "Warning", 
            "Could not load current settings from server. Using defaults."
          );
        }
      } finally {
        setIsFetchingSettings(false);
        setIsInitialized(true);
      }
    };
    
    initializeSettings();
  }, []);

  // Generic handler for setting changes
  const handleSettingChange = async (settingId: string, newValue: any) => {
    const setting = settingsConfig.find((s) => s.id === settingId);
    if (!setting) return;

    // Show confirmation dialog if required
    if (setting.confirmMessage) {
      const confirmed = await new Promise<boolean>((resolve) => {
        dispatch(
          showConfirmDialog({
            text: setting.confirmMessage!,
            title: "Confirm Action",
            intent: "primary",
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
          })
        );
      });
      
      if (!confirmed) return;
    }

    // Set loading state
    setLoadingStates((prev) => ({ ...prev, [settingId]: true }));

    try {
      // Execute the action
      await setting.action(newValue);
      
      // Update state on success
      setSettingsState((prev) => ({ ...prev, [settingId]: newValue }));
      
      // Show success message
      toastr.success("Success", setting.successMessage(newValue));
    } catch (error: any) {
      // Show error message
      const errorMsg = setting.errorMessage
        ? setting.errorMessage(error.message || error)
        : `Failed to update ${setting.label}.`;
      toastr.error("Error", errorMsg);
      
      // Revert state on error
      console.error(`Error updating ${setting.label}:`, error);
    } finally {
      // Clear loading state
      setLoadingStates((prev) => ({ ...prev, [settingId]: false }));
    }
  };

  // Render a setting based on its type
  const renderSettingControl = (setting: SettingConfig) => {
    const isLoading = loadingStates[setting.id];
    const currentValue = settingsState[setting.id];

    switch (setting.type) {
      case SettingType.TOGGLE:
        return (
          <div className="d-flex align-items-center">
            <Form.Check
              type="switch"
              id={`${setting.id}Switch`}
              label={isLoading ? "Updating..." : currentValue ? "Enabled" : "Disabled"}
              checked={currentValue}
              onChange={(e) => handleSettingChange(setting.id, e.target.checked)}
              disabled={isLoading || setting.disabled}
            />
            {isLoading && (
              <Spinner
                animation="border"
                size="sm"
                className="ml-2"
                style={{ marginLeft: "10px" }}
              />
            )}
          </div>
        );

      case SettingType.SELECT:
        return (
          <div className="d-flex align-items-center">
            <Form.Control
              as="select"
              value={currentValue}
              onChange={(e) => handleSettingChange(setting.id, e.target.value)}
              disabled={isLoading || setting.disabled}
              style={{ maxWidth: "200px" }}
            >
              {setting.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Control>
            {isLoading && (
              <Spinner
                animation="border"
                size="sm"
                className="ml-2"
                style={{ marginLeft: "10px" }}
              />
            )}
          </div>
        );

      case SettingType.BUTTON:
        return (
          <Button
            variant="primary"
            onClick={() => handleSettingChange(setting.id, true)}
            disabled={isLoading || setting.disabled}
          >
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="mr-2"
                />{" "}
                Processing...
              </>
            ) : (
              <>
                {setting.icon && <FontAwesomeIcon icon={setting.icon} className="mr-2" />}
                {setting.label}
              </>
            )}
          </Button>
        );

      default:
        return null;
    }
  };

  if (!isInitialized || isFetchingSettings) {
    return (
      <div className="manageDistrictContainer">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="sr-only">Loading settings...</span>
          </Spinner>
          <p className="mt-3 text-muted">Fetching global settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manageDistrictContainer">
      <h2 className="font-weight-bold mb-3">Global Settings</h2>
      <p>
        Manage global application settings that affect all users. Changes made here will be
        applied system-wide.
      </p>
      
      <div className="row px-3">
        <Table className="mt-4" bordered responsive>
          <thead className="purpleTableHeader">
            <tr>
              <th style={{ width: "40%" }}>Setting</th>
              <th style={{ width: "60%" }}>Control</th>
            </tr>
          </thead>
          <tbody>
            {settingsConfig.map((setting) => (
              <tr key={setting.id}>
                <td>
                  <div>
                    <strong>{setting.label}</strong>
                    {setting.description && (
                      <div>
                        <small className="text-muted">{setting.description}</small>
                      </div>
                    )}
                  </div>
                </td>
                <td>{renderSettingControl(setting)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default GlobalSettings;